/**
 * `resolveManifest` (W1-07b formats-package; contract formats/info/types.ts `ResolveManifestFn`): Phira's
 * `load_info` + `fix_info_with` (formats-and-packaging.md P§2.2, P§2.6), on a directory view of the package.
 *
 *  1. Manifest precedence info.yml > info.txt > info.csv > inferred (Phira's defaults). Only the first manifest
 *     found is read (a parse error there aborts Phira's import; there is no fallback, so its diagnostics are fatal).
 *     Text is decoded with encoding detection (`decodeText`); a legacy encoding is reported ('encodingGuessed', not
 *     fatal: Phira detects it too).
 *  2. `fix_info_with(infer_meta = no info.yml)`. An info.yml that cannot be read as a whole (invalid YAML, a root
 *     that is not a mapping) is lenient here: Phira refuses the package anyway (the fatal diagnostic stays), so META
 *     is inferred as if there were no info.yml instead of keeping Phira's defaults ("UK").
 *     - chart, music and illustration named in the manifest are used when the file exists;
 *     - a missing chart is searched in the package ROOT, in listing order (zip order), for the extensions json / pec
 *       (any case). Phira takes the first match, extra.json included, and only logs "multiple charts"; here
 *       extra.json, phiedit.json and export.json are never taken as the chart (the first other candidate is), and
 *       several candidates are reported when one was taken ('multipleCharts': Phira's pick depends on the unpacked
 *       directory order; the message names the chosen file). No chart at all is 'chartMissing'
 *       (Phira: "cannot find chart", or a parse failure when it picks extra.json);
 *     - RPE META inference: the chart is read as strict UTF-8 JSON and `META` must hold the strings name, level,
 *       background, charter and song (composer and illustration optional strings or null), else nothing is inferred.
 *       Without an info.yml, META overrides name, level, difficulty (trailing digits of the level), charter and,
 *       when present, composer and illustrator. In every case a still missing illustration / music falls back to
 *       META.background / META.song when that file exists;
 *     - music still missing: first root file with extension mp3 ogg wav flac aac; illustration still missing: png jpg
 *       jpeg bmp gif webp avif ppm. No music is 'musicMissing' (Phira imports, but cannot play it).
 * Paths are looked up as Phira's `Dir::join` resolves them: '/'-separated, a leading '/' ignored, '.' segments
 * skipped and '..' resolved (a path escaping the package names no file).
 */

import type { MessageRef } from '../../core/util/types';
import { decodeText } from './encoding';
import { readInfoCsv } from './infoCsv';
import { difficultyFromLevel, readInfoTxt } from './infoTxt';
import { defaultInfo, readInfoYmlDetailed } from './infoYml';
import type { InfoYml, ManifestDiagnostic, ManifestResult, PackageFs, ResolveManifestFn } from './types';

type MutableInfo = { -readonly [K in keyof InfoYml]: InfoYml[K] };

export const CHART_EXTENSIONS = ['json', 'pec'] as const;
export const MUSIC_EXTENSIONS = ['mp3', 'ogg', 'wav', 'flac', 'aac'] as const;
export const ILLUSTRATION_EXTENSIONS = ['png', 'jpg', 'jpeg', 'bmp', 'gif', 'webp', 'avif', 'ppm'] as const;

/** Files the chart scan passes over when another candidate exists (Phira might still pick them). */
const NOT_CHARTS: ReadonlySet<string> = new Set(['extra.json', 'phiedit.json', 'export.json']);

/**
 * A package path as Phira's `Dir::join` resolves it: backslashes are not separators, a leading '/' is ignored,
 * empty and '.' segments are skipped, '..' goes up; null when the path escapes the package root or is empty.
 */
export function resolvePackagePath(path: string): string | null {
  const out: string[] = [];
  for (const seg of path.split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      if (out.length === 0) return null;
      out.pop();
      continue;
    }
    out.push(seg);
  }
  return out.length === 0 ? null : out.join('/');
}

/** Whether `path` names a file of the package (resolved like Phira does). */
export function packageHas(fs: PackageFs, path: string): boolean {
  const p = resolvePackagePath(path);
  return p !== null && fs.has(p);
}

/** Lower-case extension of a path ('' when there is none). */
export function extensionOf(path: string): string {
  const name = path.slice(path.lastIndexOf('/') + 1);
  const dot = name.lastIndexOf('.');
  return dot <= 0 ? '' : name.slice(dot + 1).toLowerCase();
}

/** Files at the package root, in listing order. */
export function rootFiles(fs: PackageFs): string[] {
  return fs.list().filter((p) => !p.includes('/'));
}

function firstWithExtension(fs: PackageFs, exts: readonly string[]): string | undefined {
  return rootFiles(fs).find((p) => exts.includes(extensionOf(p)));
}

/** META fields Phira's metadata inference reads (P§2.6), or null when they do not deserialise. */
export interface InferredMeta {
  readonly name: string;
  readonly level: string;
  readonly background: string;
  readonly charter: string;
  readonly song: string;
  readonly composer: string | null;
  readonly illustration: string | null;
}

/** Reads `META` of a chart file the way Phira's inference does: strict UTF-8, JSON, required strings. */
export function inferMetaOf(bytes: Uint8Array): InferredMeta | null {
  let value: unknown;
  try {
    const text = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes);
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const meta = (value as Record<string, unknown>).META;
  if (meta === null || typeof meta !== 'object' || Array.isArray(meta)) return null;
  const m = meta as Record<string, unknown>;
  const req = ['name', 'level', 'background', 'charter', 'song'] as const;
  for (const k of req) if (typeof m[k] !== 'string') return null;
  const opt = (k: 'composer' | 'illustration'): string | null | undefined => {
    const v = m[k];
    if (v === undefined || v === null) return null;
    return typeof v === 'string' ? v : undefined;
  };
  const composer = opt('composer');
  const illustration = opt('illustration');
  if (composer === undefined || illustration === undefined) return null;
  return {
    name: m.name as string,
    level: m.level as string,
    background: m.background as string,
    charter: m.charter as string,
    song: m.song as string,
    composer,
    illustration,
  };
}

/** Phira's `fix_info_with` on a parsed manifest (see the module header). */
export function fixInfoWith(fs: PackageFs, input: InfoYml, inferMeta: boolean, diags: ManifestDiagnostic[]): InfoYml {
  const info: MutableInfo = { ...input };
  const has = (p: string): boolean => packageHas(fs, p);

  if (!has(info.chart)) {
    const candidates = rootFiles(fs).filter((p) => (CHART_EXTENSIONS as readonly string[]).includes(extensionOf(p)));
    const preferred = candidates.filter((p) => !NOT_CHARTS.has(p));
    const pick = preferred[0];
    // Several candidates with none usable is only 'chartMissing' (Phira would pick extra.json & co. and fail).
    if (candidates.length > 1 && pick !== undefined) {
      diags.push({ code: 'multipleCharts', detail: candidates.join(', '), phiraFatal: false });
    }
    if (pick === undefined) diags.push({ code: 'chartMissing', detail: info.chart, phiraFatal: true });
    else info.chart = pick;
  }

  const chartPath = resolvePackagePath(info.chart);
  const chartBytes = chartPath === null ? null : fs.read(chartPath);
  const meta = chartBytes === null ? null : inferMetaOf(chartBytes);
  if (meta !== null) {
    if (inferMeta) {
      info.name = meta.name;
      info.level = meta.level;
      const d = difficultyFromLevel(meta.level);
      if (d !== null) info.difficulty = d;
      info.charter = meta.charter;
      if (meta.composer !== null) info.composer = meta.composer;
      if (meta.illustration !== null) info.illustrator = meta.illustration;
    }
    if (!has(info.illustration) && has(meta.background)) info.illustration = meta.background;
    if (!has(info.music) && has(meta.song)) info.music = meta.song;
  }

  if (!has(info.music)) {
    const found = firstWithExtension(fs, MUSIC_EXTENSIONS);
    if (found === undefined) diags.push({ code: 'musicMissing', detail: info.music, phiraFatal: false });
    else info.music = found;
  }
  if (!has(info.illustration)) {
    const found = firstWithExtension(fs, ILLUSTRATION_EXTENSIONS);
    if (found !== undefined) info.illustration = found;
  }
  return info;
}

/** `resolveManifest` with what the manifest itself said, before `fix_info_with`. */
export interface ManifestResultDetailed extends ManifestResult {
  /**
   * The manifest as read, before `fix_info_with` (Phira's defaults for 'inferred'): `info.music` equal to
   * `raw.music` means the manifest names that file itself instead of it being found by META or an extension scan.
   */
  readonly raw: InfoYml;
  /** False when the manifest could not be read as a whole (its values are defaults and META inference). */
  readonly readable: boolean;
}

type ManifestReader = (text: string) => {
  readonly info: InfoYml;
  readonly diagnostics: readonly ManifestDiagnostic[];
  readonly readable?: boolean;
};

/** Phira's `load_info` + `fix_info_with` (see the module header), with the manifest as read. */
/**
 * `opts.chart`: package path of the chart to use when the package holds several (ImportOptions.chartPath); when
 * that file exists it replaces the manifest's chart before Phira's fix-ups (META inference then reads it).
 */
export function resolveManifestDetailed(fs: PackageFs, opts: { readonly chart?: string } = {}): ManifestResultDetailed {
  const chosen = opts.chart === undefined ? null : resolvePackagePath(opts.chart);
  const withChart = (info: InfoYml): InfoYml =>
    chosen !== null && packageHas(fs, chosen) ? { ...info, chart: chosen } : info;
  const diagnostics: ManifestDiagnostic[] = [];
  const sources: ReadonlyArray<readonly [string, ManifestResult['source'], ManifestReader]> = [
    ['info.yml', 'yml', readInfoYmlDetailed],
    ['info.txt', 'txt', readInfoTxt],
    ['info.csv', 'csv', readInfoCsv],
  ];
  for (const [file, source, read] of sources) {
    const bytes = fs.read(file);
    if (bytes === null) continue;
    const decoded = decodeText(bytes);
    if (decoded.encoding !== 'utf-8') {
      diagnostics.push({ code: 'encodingGuessed', detail: decoded.encoding, phiraFatal: false });
    }
    const parsed = read(decoded.text);
    diagnostics.push(...parsed.diagnostics);
    const readable = parsed.readable !== false;
    const info = fixInfoWith(fs, withChart(parsed.info), source !== 'yml' || !readable, diagnostics);
    return { source, info, encoding: decoded.encoding, diagnostics, raw: parsed.info, readable };
  }
  const raw = defaultInfo();
  const info = fixInfoWith(fs, withChart(raw), true, diagnostics);
  return { source: 'inferred', info, encoding: 'utf-8', diagnostics, raw, readable: true };
}

export const resolveManifest: ResolveManifestFn = (fs) => {
  const { raw: _raw, readable: _readable, ...result } = resolveManifestDetailed(fs);
  return result;
};

/**
 * The chart `fixInfoWith` takes from a 'multipleCharts' candidate list (`detail`, listing order joined with ', '):
 * the first candidate that is not extra.json, phiedit.json or export.json.
 */
function chosenFromCandidates(detail: string): string {
  return detail.split(', ').find((p) => p !== '' && !NOT_CHARTS.has(p)) ?? '';
}

/**
 * Translatable text of a manifest diagnostic (namespace 'info', catalogue info.messages.ts). Param `detail`: the
 * field, line, value, encoding or file name concerned ('' when there is none). 'multipleCharts' also has param
 * `chosen`, the chart file that was used: `chosenChart` when given (the resolved `ManifestResult.info.chart`), else
 * the first candidate of `detail` that is not extra.json, phiedit.json or export.json.
 */
export function manifestDiagnosticRef(d: ManifestDiagnostic, chosenChart?: string): MessageRef {
  const params = { detail: d.detail ?? '' };
  switch (d.code) {
    case 'quotedNumber':
      return { key: 'info.diag.quotedNumber', params };
    case 'quotedBool':
      return { key: 'info.diag.quotedBool', params };
    case 'badTxtLine':
      return { key: 'info.diag.badTxtLine', params };
    case 'txtHeader':
      return { key: 'info.diag.txtHeader', params };
    case 'txtParseFloat':
      return { key: 'info.diag.txtParseFloat', params };
    case 'csvEmpty':
      return { key: 'info.diag.csvEmpty', params };
    case 'encodingGuessed':
      return { key: 'info.diag.encodingGuessed', params };
    case 'yamlError':
      return { key: 'info.diag.yamlError', params };
    case 'multipleCharts':
      return {
        key: 'info.diag.multipleCharts',
        params: { ...params, chosen: chosenChart ?? chosenFromCandidates(params.detail) },
      };
    case 'chartMissing':
      return { key: 'info.diag.chartMissing', params };
    case 'musicMissing':
      return { key: 'info.diag.musicMissing', params };
  }
}
